// Canada 2026 provincial / territorial income tax. Planning estimates only, NOT tax advice.
// Brackets and basic personal amounts (BPA) for every province and territory, checked October 2026
// against CRA T4127 (edition effective 1 July 2026) as republished by catax.tools and canadianow.com.
// Each entry: brackets are cumulative upper limits; the BPA credit is worth the lowest rate.
const INF = Infinity;
export const CA_PROVINCES = {
  ON: { name: "Ontario", bpa: 12989, brackets: [[53891, 5.05], [107785, 9.15], [150000, 11.16], [220000, 12.16], [INF, 13.16]] },
  BC: { name: "British Columbia", bpa: 13216, brackets: [[50363, 5.6], [100728, 7.7], [115648, 10.5], [140430, 12.29], [190405, 14.7], [265545, 16.8], [INF, 20.5]] },
  AB: { name: "Alberta", bpa: 22769, brackets: [[61200, 8], [154259, 10], [185111, 12], [246813, 13], [370220, 14], [INF, 15]] },
  SK: { name: "Saskatchewan", bpa: 20381, brackets: [[54532, 10.5], [155805, 12.5], [INF, 14.5]] },
  MB: { name: "Manitoba", bpa: 15780, brackets: [[47000, 10.8], [100000, 12.75], [INF, 17.4]] },
  QC: { name: "Quebec", bpa: 18952, brackets: [[54345, 14], [108680, 19], [132245, 24], [INF, 25.75]], abatement: 0.165 },
  NB: { name: "New Brunswick", bpa: 13664, brackets: [[52333, 9.4], [104666, 14], [193861, 16], [INF, 19.5]] },
  NS: { name: "Nova Scotia", bpa: 11932, brackets: [[30995, 8.79], [61991, 14.95], [97417, 16.67], [157124, 17.5], [INF, 21]] },
  PE: { name: "Prince Edward Island", bpa: 15000, brackets: [[33928, 9.5], [65820, 13.47], [106890, 16.6], [142520, 17.62], [200000, 19], [INF, 20]] },
  NL: { name: "Newfoundland and Labrador", bpa: 13094, brackets: [[44678, 8.7], [89354, 14.5], [159528, 15.8], [223340, 17.8], [285319, 19.8], [570638, 20.8], [1141275, 21.3], [INF, 21.8]] },
  YT: { name: "Yukon", bpa: 16452, brackets: [[58523, 6.4], [117045, 9], [181440, 10.9], [500000, 12.8], [INF, 15]] },
  NT: { name: "Northwest Territories", bpa: 18198, brackets: [[53003, 5.9], [106009, 8.6], [172346, 12.2], [INF, 14.05]] },
  NU: { name: "Nunavut", bpa: 19659, brackets: [[55801, 4], [111602, 7], [181439, 9], [INF, 11.5]] },
};
export const CA_PROVINCE_ORDER = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
export const DEFAULT_PROVINCE = "ON";
export const provinceOf = (code) => CA_PROVINCES[code] || CA_PROVINCES[DEFAULT_PROVINCE];

function bracketsTax(income, rows) {
  let prev = 0, tax = 0;
  for (const [upTo, rate] of rows) {
    if (income <= prev) break;
    tax += (Math.min(income, upTo) - prev) * (rate / 100);
    prev = upTo;
  }
  return tax;
}

// Ontario Health Premium (income-tested, $0 to $900).
function ontarioHealthPremium(i) {
  if (i <= 20000) return 0;
  if (i <= 36000) return Math.min(300, (i - 20000) * 0.06);
  if (i <= 48000) return Math.min(450, 300 + (i - 36000) * 0.06);
  if (i <= 72000) return Math.min(600, 450 + (i - 48000) * 0.25);
  if (i <= 200000) return Math.min(750, 600 + (i - 72000) * 0.25);
  return Math.min(900, 750 + (i - 200000) * 0.25);
}

// Returns { code, name, tax, lines:[{label, amount}] } for a given taxable income.
export function provincialTax(code, income) {
  const p = provinceOf(code), c = CA_PROVINCES[code] ? code : DEFAULT_PROVINCE;
  const lowest = p.brackets[0][1] / 100;
  const gross = bracketsTax(income, p.brackets);
  const basic = Math.max(0, gross - p.bpa * lowest);
  const lines = [{ label: `${p.name} income tax (after basic personal amount)`, amount: basic }];
  if (c === "ON") {
    const surtax = Math.max(0, basic - 5818) * 0.2 + Math.max(0, basic - 7446) * 0.36;
    if (surtax > 0) lines.push({ label: "Ontario surtax", amount: surtax });
    const ohp = ontarioHealthPremium(income);
    if (ohp > 0) lines.push({ label: "Ontario Health Premium", amount: ohp });
  }
  return { code: c, name: p.name, tax: lines.reduce((s, l) => s + l.amount, 0), lines };
}

// Human-readable bracket list for the "Key numbers" panel.
export function provinceBracketText(code, m) {
  const p = provinceOf(code);
  let prev = 0;
  return p.brackets.map(([upTo, rate], i) => {
    const t = upTo === INF ? `${rate}% over ${m(prev)}` : `${rate}% ${i === 0 ? "on the first" : "to"} ${m(upTo)}`;
    prev = upTo; return t;
  }).join(", ");
}
